/* eslint-disable no-underscore-dangle */
/**
 * @class SavedFormFilters
 *
 */
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, Row, Col, Input, FormGroup, ModalBody, ModalFooter } from 'reactstrap';
import { v4 as uuidV4 } from 'uuid';
import moment from 'moment';
import Loader from '../common/loader';
import SavedFormFilter from './savedFormFilter';
import entityComposition from '../common/classes/entityComposition';
import { serializeCabinetFiltersForSavedView } from '../../utils/cabinetSavedViewFilters';
const SavedFormFilters = props => {
  const {
    login: {
      user: {
        user_id: userID
      },
      loading
    },
    user: {
      active: {
        content = {}
      } = {},
      active
    },
    cabinet = {},
    cabinetIdentifier,
    userActions: {
      serverShow,
      change,
      serverSave
    },
    replaceFilters,
    filterDefinitions
  } = props;
  const {
    indexQuery: {
      filters = {},
      sort = []
    } = {}
  } = cabinet[cabinetIdentifier] || {};
  const cabinetViews = useMemo(() => {
    let result = [];
    const {
      savedViews = {}
    } = content || {};
    if (savedViews !== undefined && savedViews[cabinetIdentifier] !== undefined) {
      result = savedViews[cabinetIdentifier];
    }
    return result;
  }, [cabinetIdentifier, content]);
  const changePath = (filterName, path) => `/content/savedViews/${cabinetIdentifier}/${filterName}/${path}`;
  const [isOpen, setIsOpen] = useState(false);
  const [currentFilterName, setCurrentFilterName] = useState('');
  useEffect(() => {
    serverShow(userID);
  }, [serverShow, userID]);
  const handleCancel = () => {
    setIsOpen(false);
  };
  const handleRemove = async removeID => {
    await change(userID, changePath(removeID, ''), null);
    const version = active.content && active.content.version || undefined;
    return serverSave(version);
  };
  const currentFilterNameChange = ev => {
    const {
      target: {
        value
      }
    } = ev;
    setCurrentFilterName(value);
  };
  const disableCreate = useMemo(() => currentFilterName.length === 0, [currentFilterName.length]);
  const handleSave = async () => {
    const now = moment();
    const newID = uuidV4().toUpperCase();
    // Shared User.content.savedViews shape (picklist / numericRange), matching iOS.
    const newFilters = serializeCabinetFiltersForSavedView(filters);
    const view = {
      id: newID,
      filters: newFilters,
      sort,
      name: currentFilterName,
      _created: now.utc().toISOString()
    };
    await change(userID, changePath(newID, ''), view);
    const version = active.content && active.content.version || undefined;
    await serverSave(version);
    setIsOpen(false);
  };

  // there are two set by default - formTypeIdentifiers and cabinetIdentifier.
  const anyFiltersSet = () => Object.values(filters).filter(value => value != null && (!Array.isArray(value) || value.length > 0) && (!(typeof value === 'string') || value.length > 0)).length > 2;
  const ids = useMemo(() => {
    let filterIDs = Object.keys(cabinetViews);
    const s = (ida, idb) => {
      const a = cabinetViews[ida];
      const b = cabinetViews[idb];
      if (a._created < b._created) {
        return 1;
      }
      if (a._created > b._created) {
        return -1;
      }
      return 0;
    };
    filterIDs = filterIDs.sort(s);
    return filterIDs;
  }, [cabinetViews]);
  return /*#__PURE__*/React.createElement("div", {
    "data-testid": "savedFormFilters"
  }, anyFiltersSet() ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Button, {
    size: "sm",
    onClick: () => setIsOpen(true),
    className: "float-md-left t-srf-save",
    "data-testid": "saveFormFilterButton"
  }, "Save Current Filters")) : undefined, /*#__PURE__*/React.createElement("div", {
    style: {
      clear: 'both',
      marginBottom: '10px'
    }
  }), ids.length > 0 ? ids.map(filterID => {
    const formFilter = cabinetViews[filterID];
    return /*#__PURE__*/React.createElement(SavedFormFilter, {
      key: formFilter.name,
      onSelect: replaceFilters,
      onRemove: () => handleRemove(filterID),
      formFilter: formFilter,
      filterDefinitions: filterDefinitions
    });
  }) : /*#__PURE__*/React.createElement("p", null, "No saved filters yet."), loading ? /*#__PURE__*/React.createElement(Loader, {
    top: "50%"
  }) : undefined, isOpen ? /*#__PURE__*/React.createElement(Modal, {
    "data-testid": "saveFormFilterModal",
    isOpen: isOpen,
    toggle: handleCancel
  }, /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement("h2", null, "Create Filter"), /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    sm: 12
  }, /*#__PURE__*/React.createElement(FormGroup, null, /*#__PURE__*/React.createElement(Input, {
    "data-testid": "filterNameInput",
    id: "filterNameInput",
    type: "text",
    name: "filterName",
    placeholder: "filter name",
    onChange: currentFilterNameChange,
    value: currentFilterName
  }))))), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Button, {
    "data-testid": "cancelFormFilterButton",
    onClick: handleCancel
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    onClick: handleSave,
    disabled: disableCreate,
    "data-testid": "createFormFilterButton"
  }, "Create"))) : undefined);
};
SavedFormFilters.propTypes = {
  cabinet: PropTypes.shape({}).isRequired,
  cabinetIdentifier: PropTypes.string.isRequired,
  user: PropTypes.shape({
    active: PropTypes.shape({
      content: PropTypes.shape({
        version: PropTypes.string
      })
    })
  }).isRequired,
  login: PropTypes.shape({
    user: PropTypes.shape({
      user_id: PropTypes.string,
      content: PropTypes.shape({})
    }),
    loading: PropTypes.bool.isRequired
  }).isRequired,
  userActions: PropTypes.shape({
    change: PropTypes.func,
    serverSave: PropTypes.func,
    serverShow: PropTypes.func
  }).isRequired,
  replaceFilters: PropTypes.func.isRequired,
  filterDefinitions: PropTypes.arrayOf(PropTypes.shape({
    index: PropTypes.string,
    title: PropTypes.string
  })).isRequired
};
export { SavedFormFilters as TestSavedFormFilters };
export default entityComposition(SavedFormFilters, ['cabinet', 'login', 'user'], {
  displaySettingsList: state => state.displaySettings.list
}, []);